
import { GameStatus } from "@/lib/types/GameTypes";
import { GuessHistory, ValidatedGuess, PreviousWord, KeyboardStatus } from "@/lib/types/GameTypes";
import * as CookieUtils from '@/lib/cookieUtils';
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';

declare global {
  interface Window {
    gtag: Function;
    cookieyes: any;
  }
}

export type CookieKey = 
  | 'game_status'
  | 'current_word'
  | 'current_level'
  | 'current_guess'
  | 'keyboard_status'
  | 'saved_guesses'
  | 'game_persistence_date'
  | 'is_transitioning'
  | 'completed_levels'
  | 'guess_history'
  | 'level_attempts'
  | 'failed_level'
  | 'previous_word'
  | 'streak'
  | 'max_streak'
  | 'last_played'
  | 'last_played_date'
  | 'version'
  | 'game_completion_state'
  | 'last_win_date'
  | 'previous_streak'
  | 'in_progress_overlay_shown'
  | 'immediate_completion'  // Added this
  | 'redirect_to_welcome'   // Added this
  | 'babblegram_word_sequence_cache';

// Default cookie prefix - DEPRECATED: Should use mode-aware prefixes
let COOKIE_PREFIX = 'babblegram_daily_'; // Changed to prevent legacy cookie creation

// Get the game mode-specific prefix
// CRITICAL: Daily mode streaks ALWAYS use base prefix for persistence across sessions
export const getStreakSpecificPrefix = (): string => {
  try {
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    console.log('🔧 Getting streak-specific prefix for mode:', currentMode);
    
    // IMPORTANT: Daily mode streaks must use base prefix to persist across sessions
    // Only practice mode uses separate streak storage for session-based streaks
    if (currentMode === 'practice') {
      console.log('🔧 Using practice-specific prefix for session streak');
      return 'babblegram_practice_';
    }
    
    // Daily and custom modes use base prefix for persistent streaks
    console.log('🔧 Using base prefix for persistent streak (daily/custom mode)');
    return 'babblegram_';
  } catch (error) {
    console.error('Error getting streak-specific prefix:', error);
    return 'babblegram_';
  }
};

export const getModeSpecificPrefix = (): string => {
  try {
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    
    console.log('Getting mode-specific prefix for mode:', currentMode);
    
    // FIXED: Use mode-aware prefixes for all modes to match ModeAwareStorageManager
    if (currentMode === 'practice') {
      return 'babblegram_practice_';
    } else if (currentMode === 'custom') {
      return 'babblegram_custom_';
    } else if (currentMode === 'daily') {
      return 'babblegram_daily_';
    }
    
    return 'babblegram_daily_'; // Default to daily prefix
  } catch (error) {
    console.error('Error getting mode-specific prefix:', error);
    return 'babblegram_daily_';
  }
};

export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  ADVERTISEMENT: 'advertisement',
  FUNCTIONALITY: 'functionality'
};

const getCookieOptions = () => {
  const isProduction = window.location.protocol === 'https:';
  
  // For Lovable preview environment, make cookies work in iframe context
  const isLovablePreview = window.location.hostname.includes('lovable.dev');
  
  return {
    path: '/',
    secure: isProduction && !isLovablePreview, // Don't require secure in Lovable preview
    sameSite: (isProduction && !isLovablePreview) ? 'strict' as const : 'lax' as const,
    expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
  };
};

export const getCookieKey = (key: CookieKey): string => {
  // CRITICAL: Prevent legacy cookie creation by ensuring all keys are mode-aware
  // NO keys should use the legacy prefix anymore
  const sharedKeys: CookieKey[] = []; // Empty - all cookies should be mode-specific now
  
  if (sharedKeys.includes(key)) {
    return `babblegram_daily_${key}`; // Even shared keys use daily prefix to prevent legacy
  }
  
  // Use mode-specific prefix for game state keys
  return `${getModeSpecificPrefix()}${key}`;
};

export const setCookie = (key: CookieKey, value: string) => {
  const fullKey = getCookieKey(key);
  console.log(`Setting cookie ${fullKey} with value ${value}`);
  
  const options = getCookieOptions();
  console.log(`Cookie options:`, options);
  
  CookieUtils.setCookie(fullKey, value, options);
  
  // Verify cookie was set
  setTimeout(() => {
    const readValue = CookieUtils.getCookie(fullKey);
    console.log(`Verification - Cookie ${fullKey} was set to: ${readValue}`);
  }, 50);
};

export const getCookie = (key: CookieKey): string | null => {
  const fullKey = getCookieKey(key);
  const value = CookieUtils.getCookie(fullKey);
  console.log(`Getting cookie ${fullKey}: ${value}`);
  
  // Check document.cookie directly to see all cookies
  if (value === null) {
    console.log(`Cookie ${fullKey} not found, all cookies:`, document.cookie);
  }
  
  return value;
};

// Add a function to get a cookie from a specific mode
export const getCookieFromMode = (key: CookieKey, mode: string): string | null => {
  try {
    // Temporarily set the access target for getModeSpecificPrefix
    localStorage.setItem('babblegram_cookie_access_target', mode);
    
    // Get the cookie with the overridden prefix
    const value = getCookie(key);
    
    // Clean up
    localStorage.removeItem('babblegram_cookie_access_target');
    
    return value;
  } catch (error) {
    console.error(`Error getting cookie ${key} from mode ${mode}:`, error);
    localStorage.removeItem('babblegram_cookie_access_target');
    return null;
  }
};

export const removeCookie = (key: CookieKey) => {
  const fullKey = getCookieKey(key);
  console.log(`Removing cookie ${fullKey}`);
  CookieUtils.deleteCookie(fullKey);
};

export const getGameStatus = (): GameStatus | null => {
  const status = getCookie('game_status');
  return status as GameStatus | null;
};

export const setGameStatus = (status: GameStatus) => {
  setCookie('game_status', status);
};

export const getCurrentWord = (): string | null => {
  return getCookie('current_word');
};

export const setCurrentWord = (word: string) => {
  setCookie('current_word', word);
};

export const getCurrentLevel = (): number | null => {
  const level = getCookie('current_level');
  return level ? parseInt(level) : null;
};

export const setCurrentLevel = (level: number) => {
  setCookie('current_level', level.toString());
};

export const getGuessHistory = (): GuessHistory | null => {
  const history = getCookie('guess_history');
  try {
    return history ? JSON.parse(history) : null;
  } catch {
    return null;
  }
};

export const setGuessHistory = (history: GuessHistory) => {
  setCookie('guess_history', JSON.stringify(history));
};

export const getLevelAttempts = (): number[] | null => {
  const attempts = getCookie('level_attempts');
  try {
    return attempts ? JSON.parse(attempts) : null;
  } catch {
    return null;
  }
};

export const setLevelAttempts = (attempts: number[]) => {
  setCookie('level_attempts', JSON.stringify(attempts));
};

export const getKeyboardStatus = (): KeyboardStatus | null => {
  const status = getCookie('keyboard_status');
  try {
    return status ? JSON.parse(status) : null;
  } catch {
    return null;
  }
};

export const setKeyboardStatus = (status: KeyboardStatus) => {
  setCookie('keyboard_status', JSON.stringify(status));
};

export const getPreviousWord = (): PreviousWord | null => {
  const word = getCookie('previous_word');
  try {
    return word ? JSON.parse(word) : null;
  } catch {
    return null;
  }
};

export const setPreviousWord = (word: PreviousWord) => {
  setCookie('previous_word', JSON.stringify(word));
};

export const getFailedLevel = (): number | null => {
  const level = getCookie('failed_level');
  return level ? parseInt(level) : null;
};

export const setFailedLevel = (level: number) => {
  setCookie('failed_level', level.toString());
};

// Streak-specific functions using simplified prefix for consistency
export const getStreak = (): number => {
  const prefix = getStreakSpecificPrefix();
  const fullKey = `${prefix}streak`;
  const streak = CookieUtils.getCookie(fullKey);
  console.log('Getting streak:', { 
    prefix, 
    fullKey, 
    streak, 
    mode: localStorage.getItem('babblegram_game_mode') 
  });
  return streak ? parseInt(streak) : 0;
};

export const setStreak = (streak: number) => {
  const prefix = getStreakSpecificPrefix();
  const fullKey = `${prefix}streak`;
  console.log('Setting streak:', { 
    streak, 
    prefix, 
    fullKey, 
    mode: localStorage.getItem('babblegram_game_mode') 
  });
  CookieUtils.setCookie(fullKey, streak.toString(), getCookieOptions());
};

export const getMaxStreak = (): number => {
  const prefix = getStreakSpecificPrefix();
  const fullKey = `${prefix}max_streak`;
  const streak = CookieUtils.getCookie(fullKey);
  console.log('Getting max streak:', { 
    prefix, 
    fullKey, 
    streak, 
    mode: localStorage.getItem('babblegram_game_mode') 
  });
  return streak ? parseInt(streak) : 0;
};

export const setMaxStreak = (streak: number) => {
  const prefix = getStreakSpecificPrefix();
  const fullKey = `${prefix}max_streak`;
  console.log('Setting max streak:', { 
    streak, 
    prefix, 
    fullKey, 
    mode: localStorage.getItem('babblegram_game_mode') 
  });
  CookieUtils.setCookie(fullKey, streak.toString(), getCookieOptions());
};

export const getLastWinDate = (): string | null => {
  const prefix = getStreakSpecificPrefix();
  const fullKey = `${prefix}last_win_date`;
  const date = CookieUtils.getCookie(fullKey);
  console.log('Getting last win date:', { 
    prefix, 
    fullKey, 
    date, 
    mode: localStorage.getItem('babblegram_game_mode') 
  });
  return date;
};

export const setLastWinDate = (date: string) => {
  const prefix = getStreakSpecificPrefix();
  const fullKey = `${prefix}last_win_date`;
  console.log('Setting last win date:', { 
    date, 
    prefix, 
    fullKey, 
    mode: localStorage.getItem('babblegram_game_mode') 
  });
  CookieUtils.setCookie(fullKey, date, getCookieOptions());
};

export const getPreviousStreak = (): number | null => {
  // Use mode-aware storage for previous streak
  const previousStreak = modeAwareStorage.getCookie('previous_streak');
  console.log('Getting previous streak:', { previousStreak });
  return previousStreak ? parseInt(previousStreak) : null;
};

export const setPreviousStreak = (streak: number) => {
  console.log('Setting previous streak:', streak);
  // Use mode-aware storage for previous streak
  modeAwareStorage.setCookie('previous_streak', streak.toString());
};

export const clearGameState = () => {
  console.log('Clearing game state for mode:', localStorage.getItem('babblegram_game_mode') || 'daily');
  
  // CRITICAL FIX: Preserve completion state for ALL modes during clearing
  // Check if there's a mode switch in progress
  const isModeSwitch = localStorage.getItem('babblegram_mode_switch_in_progress') === 'true';
  
  const cookiesToKeep: CookieKey[] = [
    'version' // Only version remains shared
  ];
  
  // During mode switches, also preserve completion state for all modes
  if (isModeSwitch) {
    console.log('Mode switch detected - preserving completion state for all modes');
    cookiesToKeep.push('game_completion_state', 'game_status');
  }
  
  const allCookies: CookieKey[] = [
    'game_status',
    'current_word',
    'current_level',
    'current_guess',
    'keyboard_status',
    'game_persistence_date',
    'is_transitioning',
    'completed_levels',
    'guess_history',
    'level_attempts',
    'failed_level',
    'previous_word',
    'game_completion_state',
    'in_progress_overlay_shown',
    'last_played_date'
  ];
  
  console.log('Cookies being kept:', cookiesToKeep.map(key => getCookieKey(key)));
  console.log('Cookies being cleared:', allCookies
    .filter(key => !cookiesToKeep.includes(key))
    .map(key => getCookieKey(key))
  );
  
  allCookies.forEach(key => {
    if (!cookiesToKeep.includes(key)) {
      removeCookie(key);
    }
  });
};

export const getLastPlayed = (): string | null => {
  // Use mode-aware storage for last played
  return modeAwareStorage.getCookie('last_played');
};

export const setLastPlayed = (date: string) => {
  // Use mode-aware storage for last played
  modeAwareStorage.setCookie('last_played', date);
};

export const handleConsentUpdate = (consent: any) => {
  console.log('Cookie consent updated:', consent);
  
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.advertisement ? "granted" : "denied",
      functionality_storage: consent.functionality ? "granted" : "denied",
    });
  }
};

export const initializeCookieConsent = () => {
  const waitForCookieYes = new Promise<void>((resolve) => {
    if (typeof window === 'undefined') return;

    if (window.cookieyes && typeof window.cookieyes === 'object') {
      resolve();
      return;
    }

    const scriptElement = document.getElementById('cookieyes');
    if (scriptElement) {
      scriptElement.addEventListener('load', () => {
        setTimeout(resolve, 100);
      });
    } else {
      console.warn('CookieYes script tag not found');
      resolve();
    }
  });

  waitForCookieYes.then(() => {
    if (window.cookieyes) {
      window.addEventListener('cookieyes_consent_update', (e: any) => {
        handleConsentUpdate(e.detail);
      });
    }
  });
};

export const clearInProgressOverlayShown = () => {
  removeCookie('in_progress_overlay_shown');
};

export const clearOverlayStates = () => {
  removeCookie('in_progress_overlay_shown');
  // Don't clear game_completion_state here - it should persist across navigations
};

// Helper for debugging mode-specific state
export const logModeStateInfo = () => {
  const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
  const prefix = getModeSpecificPrefix();
  
  console.log('Mode-specific state info:', {
    currentMode,
    prefix,
    gameStatus: getCookie('game_status'),
    currentWord: getCookie('current_word'),
    currentLevel: getCookie('current_level'),
    hasGuessHistory: !!getCookie('guess_history'),
    timestamp: new Date().toISOString()
  });
};

/**
 * Clears game state cookies, optionally preserving cookies that match specified prefixes
 * @param preservePrefixes Array of prefixes for cookies to preserve
 */
export const clearGameStateCookies = (preservePrefixes: string[] = []) => {
  const cookies = document.cookie.split(';');
  
  // Always add the current mode's prefix to preservePrefixes during mode switches
  const isModeSwitch = localStorage.getItem('babblegram_mode_switch_in_progress') === 'true';
  const targetMode = localStorage.getItem('babblegram_next_mode');
  
  if (isModeSwitch && targetMode && !preservePrefixes.includes(targetMode === 'practice' ? 'babblegram_practice_' : 'babblegram_')) {
    const targetPrefix = targetMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
    preservePrefixes.push(targetPrefix);
    console.log(`Added target mode prefix ${targetPrefix} to preservePrefixes during mode switch`);
  }
  
  console.log('Clearing game state cookies:', {
    cookieCount: cookies.length,
    preservePrefixes,
    timestamp: new Date().toISOString()
  });
  
  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    
    // Skip cookies that should be preserved
    const shouldPreserve = preservePrefixes.some(prefix => 
      cookieName.includes(prefix)
    );
    
    if (shouldPreserve) {
      console.log(`Preserving cookie: ${cookieName}`);
      return;
    }
    
    // Define exact cookie names to clear (no pattern matching)
    // Daily mode now uses proper daily-prefixed cookies only
    const cookiesToClear = [
      // Daily mode cookies (now properly prefixed)
      'babblegram_daily_guess_history',
      'babblegram_daily_game_status',
      'babblegram_daily_current_word',
      'babblegram_daily_current_level',
      'babblegram_daily_current_guess',
      'babblegram_daily_keyboard_status',
      'babblegram_daily_game_persistence_date',
      'babblegram_daily_is_transitioning',
      'babblegram_daily_completed_levels',
      'babblegram_daily_level_attempts',
      'babblegram_daily_failed_level',
      'babblegram_daily_previous_word',
      'babblegram_daily_game_completion_state',
      'babblegram_daily_in_progress_overlay_shown',
      'babblegram_daily_last_played_date',
      
      // Practice mode cookies (only clear if not preserving practice)
      'babblegram_practice_guess_history',
      'babblegram_practice_game_status',
      'babblegram_practice_current_word',
      'babblegram_practice_current_level',
      'babblegram_practice_current_guess',
      'babblegram_practice_keyboard_status',
      'babblegram_practice_game_persistence_date',
      'babblegram_practice_is_transitioning',
      'babblegram_practice_completed_levels',
      'babblegram_practice_level_attempts',
      'babblegram_practice_failed_level',
      'babblegram_practice_previous_word',
      'babblegram_practice_game_completion_state',
      'babblegram_practice_in_progress_overlay_shown',
      'babblegram_practice_last_played_date'
    ];
    
    // Only clear if this cookie is in our list
    if (cookiesToClear.includes(cookieName)) {
      console.log(`🧹 Clearing game state cookie: ${cookieName}`);
      
      CookieUtils.deleteCookie(cookieName);
      console.log(`Erased cookie: ${cookieName}`);
    }
  });
};

/**
 * Aggressively clears all state from both cookies and localStorage
 * But preserves mode-specific state during mode switches
 * @param preservePrefixes Array of prefixes for data to preserve
 */
export const aggressiveClearAllState = (preservePrefixes: string[] = []) => {
  // Always preserve the target mode's prefix during mode switches
  const isModeSwitch = localStorage.getItem('babblegram_mode_switch_in_progress') === 'true';
  if (isModeSwitch) {
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    const targetMode = localStorage.getItem('babblegram_next_mode');
    
    if (targetMode) {
      // Add the target mode's prefix to preservePrefixes if not already included
      const targetPrefix = targetMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
      if (!preservePrefixes.includes(targetPrefix)) {
        preservePrefixes.push(targetPrefix);
        console.log(`Auto-preserving target mode ${targetMode} prefix: ${targetPrefix} during mode switch`);
      }
      
      // Remember that we're switching modes
      if (!preservePrefixes.includes('babblegram_mode_switch')) {
        preservePrefixes.push('babblegram_mode_switch');
        preservePrefixes.push('babblegram_previous_mode');
        preservePrefixes.push('babblegram_next_mode');
      }
    }
  }
  
  console.log('Aggressively clearing all game state', {
    preservePrefixes,
    timestamp: new Date().toISOString()
  });
  
  // Clear cookies
  clearGameStateCookies(preservePrefixes);
  
  // Clear localStorage items
  const allKeys = Object.keys(localStorage);
  
  allKeys.forEach(key => {
    // Skip items that should be preserved
    const shouldPreserve = preservePrefixes.some(prefix => 
      key.includes(prefix)
    );
    
    if (shouldPreserve) {
      console.log(`Preserving localStorage item: ${key}`);
      return;
    }
    
    // Only clear game-related localStorage items
    if (key.includes('babblegram_') || 
        key.includes('game_') || 
        key.includes('instructions_') ||
        key.includes('streak_') ||
        key.includes('completion_')) {
      localStorage.removeItem(key);
      console.log(`Removed localStorage item: ${key}`);
    }
  });
};
